import startCase from 'lodash/startCase';
import trim from 'lodash/trim';

/**
 * Correct drm fields from system keys to human readable strings
 * @param  {String} string
 * @return {String}
 */
const fixString = (string) => {
  if (!string) { return ''; }

  return trim(startCase(string))
    .replace('Epicgames', 'Epic Games')
    .replace('Gog', 'GOG')
    .replace('Utalk', 'uTalk')
    .replace('Uplay', 'Ubisoft Connect')
    .replace('Esonline', 'ES Online')
    .replace('xbox', 'XBOX')
    .replace('Rockstar', 'Rockstar Social Club');
};

/**
 * Format a single drm string or products.drm object into a human readable string
 * @param  {String|Object} drm      DRM String or Product DRM object
 * @param  {String} [separator=','] Separator for splitting input string and joining output string
 * @return {String}
 *
 * @example
 * formatDrmName({steam: true, gog: false, uplay: true}, ' ')
 * // returns 'Steam Uplay'
 *
 * @example
 * formatDrmName('steam')
 * // returns 'Steam'
 *
 * @example
 * formatDrmName('steam,gog')
 * // returns 'Steam, GOG'
 *
 */
export function formatDrmName(drm, separator = ',') {
  if (!drm) {
    return null;
  }

  if (typeof drm === 'string') {
    if (drm.includes(separator)) {
      return drm.split(separator)
        .map(fixString)
        .join(`${separator} `);
    }
    return fixString(drm);
  }

  if (typeof drm === 'object' && Object.prototype.hasOwnProperty.call(drm, 'steam')) {
    return Object.keys(drm)
      .filter(k => drm[k])
      .map(fixString)
      .join(`${separator} `);
  }

  return null;
}

/**
 * Format a single platofmr string or products.platforms object into a human readable string
 * Sort so that Windows is always first
 * @param  {String|Object} platforms  Platform String or Product platforms object
 * @param  {String} [separator=',']   Separator for splitting input string and joining output string
 * @return {String}
 *
 * @example
 * formatDrmName({linux: true, mac: false, windows: true}, ' ')
 * // returns 'Windows Mac'
 *
 * @example
 * formatDrmName('windows')
 * // returns 'Windows'
 *
 * @example
 * formatDrmName('windows,mac')
 * // returns 'Windows, Mac'
 *
 */
export function formatPlatformsName(platforms, separator = ',') {
  if (!platforms) {
    return null;
  }

  if (typeof platforms === 'string') {
    if (platforms.includes(separator)) {
      return platforms.split(separator)
        .map(fixString)
        .join(`${separator} `);
    }
    return fixString(platforms);
  }

  if (typeof platforms === 'object' && Object.prototype.hasOwnProperty.call(platforms, 'windows')) {
    return Object.keys(platforms)
      .filter(k => platforms[k])
      .sort()
      .reverse()
      .map(fixString)
      .join(`${separator} `);
  }

  return null;
}
